import React, {Component, ReactNode} from "react";
import GuessInput from "./GuessInput";
import {Button, Container, IconButton, Stack, Typography} from "@mui/material";
import ResultTable from "../resulttable/ResultTable";
import {GameContext, GameStatus, Guess} from '../content/GameContext';
import {CompletionPopup} from './CompletionPopup';
import {instanceOf} from "prop-types";
import {Cookies, withCookies} from 'react-cookie';
import {DateUtils} from './DateUtils';
import {SilhouettePopup} from './SilhouettePopup';
import {BarChartOutlined, HelpOutline, SettingsOutlined} from '@mui/icons-material';
import {HelpPopup} from './HelpPopup';
import {StatsPopup} from './StatsPopup';

interface GameState<T> {
    guess: T | null;
    numGuess: number;
    guesses: Guess<T>[];
    status: GameStatus;
    showDialog: boolean;
    showHelp: boolean;
    showStats: boolean;
}

interface GameProperties<T> {
    startTime: Date,
    context: GameContext<T>;
    cookies: Cookies;
}

export interface GameOption<T> {
    value: T,
    label: string
}

const STATE_COOKIE_KEY = "gamestate";
const LAST_GAME_PLAYED_KEY = "lastgame";
const GAMES_COOKIE_KEY = "totalgames";
const WINS_COOKIE_KEY = "totalwins";
const CURRENT_STREAK_COOKIE_KEY = "currentstreak";
const MAX_STREAK_COOKIE_KEY = "maxstreak";
const TOTAL_GUESSES_COOKIE_KEY = "totalguesses";

class Game<T> extends Component<GameProperties<T>, GameState<T>> {
    static propTypes = {
        cookies: instanceOf(Cookies).isRequired
    };

    constructor(props: GameProperties<T>) {
        super(props);
        this.state = {
            guess: null,
            numGuess: 0,
            guesses: [],
            status: GameStatus.InProgress,
            showDialog: false,
            showHelp: false,
            showStats: false,
        };
    }

    override componentDidMount() {
        // loads the picks from cookies
        let cached = this.props.cookies.get(this.getCookie(STATE_COOKIE_KEY)) as T[];
        if (cached == null) {
            return;
        }
        const guesses = cached.map((item, index) => this.props.context.processGuess(item, index + 1));
        const gameState = this.props.context.getState(guesses);
        // Loading Gamestate
        this.setState(() => (
            {
                numGuess: cached.length,
                guesses: guesses,
                guess: null,
                selectedObj: null,
                status: gameState,
                showDialog: gameState !== GameStatus.InProgress,
                showHelp: false,
                showStats: false
            }
        ), () => {
            // Cache loaded
        });
    }

    private getCookie = (key: string) => {
        return `${this.props.context.title}_${key}`;
    };

    private optionSelected = (guess: GameOption<T> | null) => {
        if (guess == null) {
            return;
        }
        const guessObj = this.props.context.processGuess(guess.value, this.state.numGuess + 1);
        this.setState(prevState => {
                const newGuesses = this.state.guesses.concat(guessObj);
                const gameState = this.props.context.getState(newGuesses);
                return {
                    numGuess: prevState.numGuess + 1,
                    guesses: newGuesses,
                    guess: null,
                    status: gameState,
                    showDialog: gameState !== GameStatus.InProgress,
                };
            }, () => {
                let data = JSON.stringify(this.state.guesses.map(guess => guess.getOption()));
                this.props.cookies.set(this.getCookie(STATE_COOKIE_KEY), data, {
                    path: '/',
                    expires: DateUtils.GetMidnight()
                });
                if (this.state.status !== GameStatus.InProgress) {
                    let gameNumber = this.props.cookies.get(this.getCookie(LAST_GAME_PLAYED_KEY)) || 0;
                    if (gameNumber !== this.props.context.getGameNumber()) {
                        this.props.cookies.set(this.getCookie(LAST_GAME_PLAYED_KEY), this.props.context.getGameNumber(), {
                            path: '/'
                        });
                        let gamesPlayed = this.props.cookies.get(this.getCookie(GAMES_COOKIE_KEY)) || 0;
                        this.props.cookies.set(this.getCookie(GAMES_COOKIE_KEY), gamesPlayed + 1, {
                            path: '/'
                        });
                        if (this.state.status === GameStatus.Success) {
                            let wins = this.props.cookies.get(this.getCookie(WINS_COOKIE_KEY)) || 0;
                            this.props.cookies.set(this.getCookie(WINS_COOKIE_KEY), wins + 1, {
                                path: '/'
                            });
                            // Get Current Streak
                            let streak = this.props.cookies.get(this.getCookie(CURRENT_STREAK_COOKIE_KEY)) || 0;
                            this.props.cookies.set(this.getCookie(CURRENT_STREAK_COOKIE_KEY), streak + 1, {
                                path: '/',
                                expires: DateUtils.GetNextMidnight(this.props.startTime)
                            });
                            // Set Max Streak
                            const maxStreak = Math.max(streak, this.props.cookies.get(this.getCookie(MAX_STREAK_COOKIE_KEY)) || 0);
                            this.props.cookies.set(this.getCookie(MAX_STREAK_COOKIE_KEY), maxStreak + 1, {
                                path: '/'
                            });
                            // Set Total Guesses
                            const totalGuesses = this.props.cookies.get(this.getCookie(TOTAL_GUESSES_COOKIE_KEY)) || 0;
                            this.props.cookies.set(this.getCookie(TOTAL_GUESSES_COOKIE_KEY), totalGuesses + this.state.guesses.length, {
                                path: '/'
                            });
                        }
                    }
                }
            }
        );
    };

    private primaryButtonClicked = () => {
        this.setState(() => ({
            showDialog: true
        }));
    };

    private onPrimaryDialogClosed = () => {
        this.setState(() => ({
            showDialog: false
        }));
    };

    private openHelpDialog = () => {
        this.setState(() => ({
            showHelp: true
        }));
    };

    private onHelpDialogClosed = () => {
        this.setState(() => ({
            showHelp: false
        }));
    };

    private openStatsDialog = () => {
        this.setState(() => ({
            showStats: true
        }));
    };

    private onStatsDialogClosed = () => {
        this.setState(() => ({
            showStats: false
        }));
    };

    private getHeaderRow = () => {
        return <Stack sx={{backgroundColor: "primary.main", width: "100%"}}
                      direction="row"
                      position={"relative"}>
            <IconButton sx={{color: "secondary.main", marginLeft: "auto", flex: "0 1 auto"}}
                        onClick={this.openStatsDialog}>
                <BarChartOutlined/>
            </IconButton>
            <IconButton sx={{color: "secondary.main"}} onClick={this.openHelpDialog}>
                <HelpOutline/>
            </IconButton>
            <IconButton sx={{color: "secondary.main"}}>
                <SettingsOutlined/>
            </IconButton>
        </Stack>;
    };

    override render(): ReactNode {
        const buttonText = this.state.status !== GameStatus.InProgress ? "View Result" : "Show Silhouette";
        return <Container maxWidth={false}
                          sx={{width: "100%", height: "100%", backgroundColor: "background.default"}}
                          disableGutters>
            {this.getHeaderRow()}
            <Stack
                direction="column"
                spacing={2}
                alignItems={"center"}
                sx={{paddingY: "2%", paddingX: "4%", height: "94%"}}>
                <Typography sx={{color: "primary.main"}}
                            fontWeight={'bold'}
                            textAlign={"center"}
                            variant={"h4"}>
                    {this.props.context.title}
                </Typography>
                <GuessInput handleInput={this.optionSelected}
                            options={this.props.context.getOptions(this.state.guesses.map(guess => guess.getOption()))}
                            totalGuesses={this.props.context.totalGuesses}
                            previousGuesses={this.state.guesses}
                            gameStatus={this.state.status}
                />
                {
                    (this.props.context.showSilhouette || this.state.status !== GameStatus.InProgress) ?
                        <Button variant="outlined" onClick={this.primaryButtonClicked}>{buttonText}</Button> :
                        <Container/>
                }
                <ResultTable columns={this.props.context.getColumnDetails()}
                             widths={this.props.context.getColumnWidths()}
                             rows={this.props.context.getGuessDisplayProperties(this.state.guesses)}
                />
            </Stack>
            <SilhouettePopup show={this.state.showDialog && this.state.status === GameStatus.InProgress}
                             onClose={this.onPrimaryDialogClosed}
                             gameContext={this.props.context}/>
            <CompletionPopup show={this.state.showDialog && this.state.status !== GameStatus.InProgress}
                             successful={this.state.status === GameStatus.Success}
                             onClose={this.onPrimaryDialogClosed}
                             gameContext={this.props.context}
                             guesses={this.state.guesses}/>
            <HelpPopup show={this.state.showHelp} onClose={this.onHelpDialogClosed}
                       gameContext={this.props.context}/>
            <StatsPopup show={this.state.showStats} onClose={this.onStatsDialogClosed}
                        gameContext={this.props.context}
                        maxStreak={this.props.cookies.get(this.getCookie(MAX_STREAK_COOKIE_KEY)) || 0}
                        streak={this.props.cookies.get(this.getCookie(CURRENT_STREAK_COOKIE_KEY)) || 0}
                        gamesPlayed={this.props.cookies.get(this.getCookie(GAMES_COOKIE_KEY)) || 0}
                        totalGuesses={this.props.cookies.get(this.getCookie(TOTAL_GUESSES_COOKIE_KEY)) || 0}
                        wins={this.props.cookies.get(this.getCookie(WINS_COOKIE_KEY)) || 0}
            />
        </Container>;
    }
}

export default withCookies(Game);
